import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  ArenaSparChatRequest,
  ArenaSparChatResponse,
  ChatMessage,
} from '@arena/shared';
import { SceneBack } from '@/shared/components/SceneBack';
import { findCharacter, portraitFor } from '../data/characters';
import './SparChat.css';

// Same-origin by default; Vite proxies /api/* to wrangler dev. Avoids
// cross-origin permission prompts on Safari / macOS / firewalls.
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

/**
 * Sparring chat screen — /arena/spar/:id.
 *
 * The user has chosen an adversary on /arena/spar; this screen drops them into
 * a back-and-forth with that figure. Messages are kept in component state (no
 * persistence) — sessions are intentionally ephemeral. Each turn posts the
 * full history to /api/arena/spar/chat which roleplays as the figure using
 * their worldview file.
 */
export function SparChat() {
  const { id } = useParams<{ id: string }>();
  const adversary = useMemo(() => findCharacter(id ?? null), [id]);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  /** Index of the most recent user message we've already scrolled into view —
   * lets us snap to the new user turn exactly once per send, without re-snapping
   * when the assistant's reply lands. */
  const lastScrolledUserIdxRef = useRef<number>(-1);

  useEffect(() => {
    if (adversary) {
      document.title = `Sparring ${adversary.name} — The Arena`;
    } else {
      document.title = 'Sparring — The Arena';
    }
  }, [adversary]);

  /**
   * On each new *user* message, bring it to the top of the scroll area so the
   * user has the assistant's response growing into view as it arrives. We
   * deliberately do NOT auto-scroll on assistant turns — that would yank the
   * user to the bottom of long responses they're trying to read top-down.
   */
  useEffect(() => {
    let lastUserIdx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        lastUserIdx = i;
        break;
      }
    }
    if (lastUserIdx === -1 || lastUserIdx === lastScrolledUserIdxRef.current) return;
    lastScrolledUserIdxRef.current = lastUserIdx;

    const container = scrollRef.current;
    if (!container) return;
    const target = container.querySelector<HTMLDivElement>(
      `[data-msg-idx="${lastUserIdx}"]`,
    );
    if (!target) return;
    const offset =
      target.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop -
      16;
    container.scrollTo({ top: Math.max(offset, 0), behavior: 'smooth' });
  }, [messages]);

  if (!adversary) {
    return (
      <main className="scene">
        <div className="scene__image scene__image--spar" role="img" aria-label="The arena" />
        <div className="scene__vignette" />
        <SceneBack />
        <div className="scene__content">
          <p className="scene__eyebrow">— Missing —</p>
          <h1 className="scene__title">No adversary chosen</h1>
          <p className="scene__subtitle">
            Return to the ring and choose someone to face.
          </p>
          <Link to="/arena/spar" className="scene__cta">
            Return
          </Link>
        </div>
      </main>
    );
  }

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setError(null);
    const next: ChatMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setSending(true);

    try {
      const body: ArenaSparChatRequest = {
        characterId: adversary.id,
        messages: next,
      };
      const res = await fetch(`${API_BASE}/api/arena/spar/chat`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => ({}))) as {
          error?: string;
          detail?: string;
        };
        throw new Error(payload.detail ?? payload.error ?? `HTTP ${res.status}`);
      }
      const data = (await res.json()) as ArenaSparChatResponse;
      setMessages((m) => [...m, data.message]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <main className="scene scene--spar-chat">
      <div
        className="scene__image scene__image--spar"
        role="img"
        aria-label="The arena"
      />
      <div className="scene__vignette" />
      <SceneBack />

      <div className="spar-chat">
        <header className="spar-chat__header">
          <img
            className="spar-chat__portrait"
            data-portrait={adversary.id}
            src={portraitFor(adversary.id)}
            alt=""
            loading="eager"
          />
          <div className="spar-chat__title-block">
            <p className="spar-chat__eyebrow">— Across the floor —</p>
            <h1 className="spar-chat__name">{adversary.name}</h1>
            <p className="spar-chat__meta">
              <span>{adversary.era}</span>
              <span aria-hidden="true" className="spar-chat__meta-dot">
                ·
              </span>
              <span>{adversary.tradition}</span>
            </p>
          </div>
        </header>

        <div className="spar-chat__messages-frame">
          <div className="spar-chat__messages" ref={scrollRef}>
            {messages.length === 0 && (
              <p className="spar-chat__empty">
                <em>{adversary.name}</em> is waiting. Open the exchange.
              </p>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                data-msg-idx={i}
                className={`spar-chat__msg spar-chat__msg--${m.role}`}
              >
                <span className="spar-chat__role">
                  {m.role === 'user' ? 'You' : adversary.name}
                </span>
                <p className="spar-chat__content">{m.content}</p>
              </div>
            ))}

            {sending && (
              <div className="spar-chat__msg spar-chat__msg--assistant spar-chat__msg--pending">
                <span className="spar-chat__role">{adversary.name}</span>
                <p className="spar-chat__content">
                  <span className="spar-chat__dot" />
                  <span className="spar-chat__dot" />
                  <span className="spar-chat__dot" />
                </p>
              </div>
            )}

            {error && (
              <div className="spar-chat__error" role="alert">
                {error}
              </div>
            )}
          </div>
        </div>

        <div className="spar-chat__compose">
          <textarea
            className="spar-chat__input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={`Speak to ${adversary.name}…`}
            rows={3}
            spellCheck={false}
            disabled={sending}
            autoFocus
          />
          <button
            type="button"
            className="spar-chat__send"
            onClick={handleSend}
            disabled={!input.trim() || sending}
          >
            Send
          </button>
        </div>
      </div>
    </main>
  );
}
